import React, { useState } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, Scale, Save, Loader2 } from "lucide-react";
import { sectionFromEditor, sectionToEditor } from "@/lib/legalPagesPayload";

const TABS = [
  { id: "operator", label: "Cégadatok" },
  { id: "terms", label: "ÁSZF" },
  { id: "privacy", label: "Adatvédelem" },
];

const inputCls =
  "w-full bg-anthracite-900 border border-white/10 rounded-lg p-2.5 text-sm focus:border-electric outline-none";

export function AdminLegalPagesEditor({ legalPages, onChange, onSave, saving = false }) {
  const [tab, setTab] = useState("terms");
  const lp = legalPages || { operator: {}, terms: { sections: [] }, privacy: { sections: [] } };

  const setOperator = (patch) => onChange({ ...lp, operator: { ...(lp.operator || {}), ...patch } });

  const docKey = tab === "privacy" ? "privacy" : tab === "terms" ? "terms" : null;
  const doc = docKey ? lp[docKey] || { title: "", sections: [] } : null;
  const sections = docKey
    ? [...(doc.sections || [])].map(sectionToEditor).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    : [];

  const setDoc = (patch) => {
    if (!docKey) return;
    onChange({ ...lp, [docKey]: { ...doc, ...patch } });
  };

  const setSections = (nextEditorSections) => {
    setDoc({ sections: nextEditorSections.map(sectionFromEditor) });
  };

  const updateSection = (i, patch) => {
    const next = [...sections];
    next[i] = { ...next[i], ...patch };
    setSections(next);
  };

  const moveSection = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    const next = [...sections];
    const oA = next[i].sort_order ?? i * 10;
    const oB = next[j].sort_order ?? j * 10;
    next[i] = { ...next[i], sort_order: oB };
    next[j] = { ...next[j], sort_order: oA };
    setSections(next);
  };

  return (
    <div className="glass rounded-2xl p-5" data-testid="admin-legal-pages">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Scale className="w-4 h-4 text-electric" strokeWidth={1.5} />
          <h3 className="font-heading text-lg">ÁSZF és adatvédelem</h3>
        </div>
        {onSave && (
          <button
            type="button"
            data-testid="admin-legal-save"
            disabled={saving}
            onClick={() => onSave(lp)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-electric hover:bg-electric-light disabled:opacity-50 text-white text-sm font-medium"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Jogi oldalak mentése
          </button>
        )}
      </div>

      <p className="text-xs text-slate-500 mb-4">
        Placeholderek a cégadatoknál: {"{companyName}"}, {"{seat}"}, {"{email}"}, {"{effectiveDate}"} stb. — a
        nyilvános oldalon automatikusan kicserélődnek. Bekezdés: üres sor. Lista: soronként egy pont.{" "}
        <span className="text-amber-400/90">
          Deploy / API restart felülírja a MongoDB tartalmat a repó{" "}
          <code className="text-[10px]">default_legal_pages.json</code> fájlból — tartós módosítás ott (revision
          növelése).
        </span>
      </p>

      <div className="inline-flex rounded-full border border-white/10 p-0.5 mb-6">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`admin-legal-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-full text-xs transition-colors ${
              tab === t.id ? "bg-electric text-white" : "text-slate-400 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "operator" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            ["brand", "Márkanév"],
            ["companyName", "Cég teljes neve"],
            ["seat", "Székhely"],
            ["registry", "Cégjegyzékszám"],
            ["taxNumber", "Adószám"],
            ["email", "E-mail"],
            ["website", "Weboldal"],
            ["effectiveDate", "Hatály / frissítés dátuma"],
          ].map(([key, label]) => (
            <div key={key}>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
              <input
                data-testid={`admin-legal-op-${key}`}
                value={lp.operator?.[key] || ""}
                onChange={(e) => setOperator({ [key]: e.target.value })}
                className={inputCls}
              />
            </div>
          ))}
        </div>
      )}

      {docKey && (
        <>
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Oldal címe</div>
            <input
              data-testid={`admin-legal-${docKey}-title`}
              value={doc.title || ""}
              onChange={(e) => setDoc({ title: e.target.value })}
              className={inputCls}
            />
          </div>

          <div className="space-y-4">
            {sections.map((sec, i) => (
              <div key={sec.id || i} className="bg-white/[0.03] rounded-xl p-4 border border-white/5 space-y-3">
                <div className="flex flex-wrap justify-between gap-2">
                  <span className="text-xs text-slate-500">§ {i + 1}</span>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => moveSection(i, -1)} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400">
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => moveSection(i, 1)} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setSections(sections.filter((_, x) => x !== i))}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-red-500/10 text-red-400"
                    >
                      <Trash2 className="w-3 h-3" /> Törlés
                    </button>
                  </div>
                </div>
                <input
                  value={sec.title || ""}
                  onChange={(e) => updateSection(i, { title: e.target.value })}
                  placeholder="Fejezet címe"
                  className={inputCls}
                />
                <textarea
                  rows={5}
                  value={sec.bodyText || ""}
                  onChange={(e) => updateSection(i, { bodyText: e.target.value })}
                  placeholder="Bekezdések — üres sorral elválasztva"
                  className={`${inputCls} resize-y min-h-[100px] font-mono text-[13px]`}
                />
                <textarea
                  rows={3}
                  value={sec.listText || ""}
                  onChange={(e) => updateSection(i, { listText: e.target.value })}
                  placeholder="Felsorolás — soronként egy elem"
                  className={`${inputCls} resize-y font-mono text-[13px]`}
                />
              </div>
            ))}
          </div>

          <button
            type="button"
            data-testid={`admin-legal-${docKey}-add`}
            onClick={() =>
              setSections([
                ...sections,
                sectionToEditor({
                  id: `legal_${Date.now()}`,
                  title: "",
                  body: [],
                  list: [],
                  sort_order: (sections.length + 1) * 10,
                }),
              ])
            }
            className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full glass hover:bg-white/10"
          >
            <Plus className="w-3.5 h-3.5" /> Új fejezet
          </button>
        </>
      )}
    </div>
  );
}
